import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import "./QuizTimer.css";

const QuizTimer = ({
  timeLimitMinutes,
  remainingTimeSeconds,
  onTimeUp,
  isActive = true,
  className = "",
}) => {
  const [timeLeft, setTimeLeft] = useState(
    remainingTimeSeconds || timeLimitMinutes * 60
  );
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calculate display values
  const hours = Math.floor(timeLeft / 3600);
  const minutes = Math.floor((timeLeft % 3600) / 60);
  const seconds = timeLeft % 60;

  // Format time display
  const formatTime = (h, m, s) => {
    if (h > 0) {
      return `${h}:${m.toString().padStart(2, "0")}:${s
        .toString()
        .padStart(2, "0")}`;
    }
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  // Handle time up
  const handleTimeUp = useCallback(async () => {
    if (isSubmitting) return; // Prevent multiple calls

    setIsSubmitting(true);
    try {
      await onTimeUp();
    } catch (error) {
      console.error("Error handling time up:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [onTimeUp, isSubmitting]);

  // Timer effect
  useEffect(() => {
    if (!isActive || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        const newTime = prev - 1;

        // Update warning states
        if (newTime <= 60 && !isCritical) {
          setIsCritical(true);
          setIsWarning(false);
        } else if (newTime <= 300 && !isWarning && !isCritical) {
          setIsWarning(true);
        }

        // Handle time up
        if (newTime <= 0) {
          clearInterval(timer);
          handleTimeUp();
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isActive, timeLeft, handleTimeUp, isWarning, isCritical]);

  // Reset timer when timeLimitMinutes or remainingTimeSeconds changes
  useEffect(() => {
    if (remainingTimeSeconds !== null) {
      setTimeLeft(remainingTimeSeconds);
    } else if (timeLimitMinutes) {
      setTimeLeft(timeLimitMinutes * 60);
    }
    setIsWarning(false);
    setIsCritical(false);
  }, [timeLimitMinutes, remainingTimeSeconds]);

  if (!timeLimitMinutes || timeLimitMinutes <= 0) {
    return null; // Don't show timer if no time limit
  }

  const displayTime = formatTime(hours, minutes, seconds);

  return (
    <Box className={`quiz-timer ${className}`}>
      <Typography variant="caption" className="quiz-timer-label">
        Time Remaining
      </Typography>

      <Box className="quiz-timer-display">
        <Typography
          variant="h6"
          className={`quiz-timer-text ${
            isCritical ? "critical" : isWarning ? "warning" : "normal"
          }`}
        >
          {displayTime}
        </Typography>
      </Box>
    </Box>
  );
};

export default QuizTimer;
